import React, { useEffect, useRef, useState } from 'react'
import { AlertCircle, Save } from 'react-feather'
import {
  Box,
  Button,
  Chip,
  Container,
  makeStyles,
  Typography,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { projectVar } from '../../../apollo/reactive-vars'
import {
  BemCorrectionFactorsData,
  BemCorrectionFactorsDataInput,
  BuildingEnergyModelFragment,
  ConsumptionEnergyType,
  HourSeriesType,
  UpdateBuildingEnergyModelInput,
} from '../../../generated/graphql'
import { BemSummaryCharts } from '../summary/BemSummaryCharts'
import { theme } from '../../../theme'
import {
  calculateHourSeries,
  calculateHourSerieWithFactors,
} from '../../../energy-system/hour-series-calculator'
import { HourSeriesResult } from '../../../building-energy-model/hour-series-calculator'
import { BemSummaryTable } from '../summary/BemSummaryTable'
import { useBuildingEnergyModels } from '../../../building-energy-model/use-building-energy-models'
import { initialCorrectionFactorsData } from '../summary/summary-util'
import { hoursInYear } from '../../../util/hours-in-year'
import { sum, sumItems } from '../../../util/array-utility'
import { BemSummaryElectricityTable } from '../summary/BemSummaryElectricityTable'
import { calcElectricityHourSeries } from '../../../energy-system/electricity-consumption-hour-series-calculator'
import { EmptyTableContainer } from '../summary/EmptyTableContainer'
import { LoadingDialog } from '../../common/LoadingDialog'

const calcSeriesSumSerie = (
  hourSeries: HourSeriesResult[],
  seriesType: HourSeriesType,
): number[] => {
  const series = hourSeries
    .filter((s) => s.hourSerieType === seriesType)
    .map((serie) => serie.hourSerie)

  let resultSerie = Array<number>(hoursInYear).fill(0)
  if (series.length === 0) return resultSerie

  resultSerie = sumItems(series)

  return resultSerie
}

const getSeriesFactorData = (
  hourSeries: HourSeriesResult[],
  correctionFactors: BemCorrectionFactorsData[],
  seriesType: HourSeriesType,
): BemCorrectionFactorsData[] => {
  const resultFactors: BemCorrectionFactorsData[] = []

  const series = hourSeries.filter((s) => s.hourSerieType === seriesType)

  series.forEach((serie) => {
    const factorDataIndex = correctionFactors.findIndex(
      (f) => f.energyType === serie.consumptionEnergyType,
    )
    if (factorDataIndex >= 0)
      resultFactors.push({ ...correctionFactors[factorDataIndex] })
  })

  return resultFactors
}

const calcSeriesWithFactors = (
  hourSeries: HourSeriesResult[],
  correctionFactors: BemCorrectionFactorsData[],
): HourSeriesResult[] => {
  const resultSeries: HourSeriesResult[] = []

  hourSeries.forEach((serie) => {
    const factor = correctionFactors.find(
      (f) => f.energyType === serie.consumptionEnergyType,
    )
    if (factor) {
      const tempSerie = calculateHourSerieWithFactors(serie, factor)

      resultSeries.push(tempSerie)
    }
  })

  return resultSeries
}

const useStyles = makeStyles((themeSpec) => ({
  warningContainer: {
    color: 'white',
    backgroundColor: `${themeSpec.palette.warning.main} !important`,
    '&:hover': {
      backgroundColor: themeSpec.palette.warning.main,
      filter: 'brightness(120%)',
    },
  },
  chipIcon: {
    color: 'white',
  },
}))

type Props = {
  buildingEnergyModel: BuildingEnergyModelFragment
}

export function BemSummaryTab({
  buildingEnergyModel,
}: Props): React.ReactElement {
  const [hourSeries, setHourSeries] = useState<HourSeriesResult[]>([])

  const [correctionFactors, setCorrectionFactors] = useState<
    BemCorrectionFactorsData[]
  >(buildingEnergyModel.correctionFactors ?? initialCorrectionFactorsData())

  const heatingSumSerieRef = useRef<number[]>([])
  const coolingSumSerieRef = useRef<number[]>([])
  const electricitySumSerieRef = useRef<number[]>([])

  const hourSeriesWithFactorsRef = useRef<HourSeriesResult[]>([])

  const hourSeriesSumsWithoutFactors = useRef<
    Partial<Record<ConsumptionEnergyType, number>>
  >({})

  const updatedHourSeriesTypesRef = useRef<HourSeriesType[]>([])

  const dbUpdateNeededRef = useRef(false)

  const correctionFactorsRef =
    useRef<BemCorrectionFactorsData[]>(correctionFactors)

  const classes = useStyles()
  const { t } = useTranslation()

  const project = useReactiveVar(projectVar)

  const projectCalendarYear = project?.calendarYear ?? null

  const { updateBuildingEnergyModel, isUpdateLoading } =
    useBuildingEnergyModels()

  const saveBem = async () => {
    const factors = correctionFactorsRef.current.map(
      (f) =>
        ({
          energyType: f.energyType,
          correctionFactor: f.correctionFactor,
          energyFactor: f.energyFactor,
        } as BemCorrectionFactorsDataInput),
    )

    const input: Partial<UpdateBuildingEnergyModelInput> = {
      correctionFactors: factors,
    }

    await updateBuildingEnergyModel(buildingEnergyModel.id, input)

    dbUpdateNeededRef.current = false
  }

  useEffect(() => {
    if (
      buildingEnergyModel.energySystems.length > 0 &&
      project?.temperatureHourSeries?.values &&
      projectCalendarYear
    ) {
      const series = calculateHourSeries(
        buildingEnergyModel,
        project.temperatureHourSeries.values,
        projectCalendarYear,
      )

      series.push(
        ...calcElectricityHourSeries(
          buildingEnergyModel,
          project?.temperatureHourSeries?.values ?? [],
          buildingEnergyModel.area,
          projectCalendarYear,
        ),
      )

      series.forEach((s) => {
        hourSeriesSumsWithoutFactors.current[s.consumptionEnergyType] = sum(
          s.hourSerie,
        )
      })

      hourSeriesWithFactorsRef.current = calcSeriesWithFactors(
        series,
        correctionFactors,
      )

      heatingSumSerieRef.current = calcSeriesSumSerie(
        hourSeriesWithFactorsRef.current,
        HourSeriesType.HeatingDemand,
      )

      coolingSumSerieRef.current = calcSeriesSumSerie(
        hourSeriesWithFactorsRef.current,
        HourSeriesType.CoolingDemand,
      )

      electricitySumSerieRef.current = calcSeriesSumSerie(
        hourSeriesWithFactorsRef.current.filter(
          (s) =>
            s.consumptionEnergyType !==
              ConsumptionEnergyType.OccupantsElectricity &&
            s.consumptionEnergyType !==
              ConsumptionEnergyType.PropertyElectricity,
        ),
        HourSeriesType.ElectricityDemand,
      )

      setHourSeries(series)
    }

    return () => {
      if (
        dbUpdateNeededRef.current &&
        correctionFactorsRef.current.length > 0
      ) {
        void saveBem()
      }
    }
  }, [buildingEnergyModel]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateFactorData = (
    energyType: ConsumptionEnergyType,
    fieldName: keyof Pick<
      BemCorrectionFactorsData,
      'correctionFactor' | 'energyFactor'
    >,
    factor: string,
  ): boolean => {
    const factorDataIndex = correctionFactors.findIndex(
      (f) => f.energyType === energyType,
    )
    if (factorDataIndex < 0) return false
    if (factor.trim() === '') return false

    const factorNumber = Number(factor.replace(/\s/g, '').replace(',', '.'))

    if (Number.isNaN(factorNumber)) return false

    const updatedFactors = correctionFactors.map((item, index) =>
      index === factorDataIndex ? { ...item, [fieldName]: factorNumber } : item,
    )

    const hourSerieIndex = hourSeriesWithFactorsRef.current.findIndex(
      (s) => s.consumptionEnergyType === energyType,
    )
    if (hourSerieIndex >= 0) {
      hourSeriesWithFactorsRef.current[hourSerieIndex] =
        calculateHourSerieWithFactors(
          hourSeries[hourSerieIndex],
          updatedFactors[factorDataIndex],
        )

      switch (hourSeriesWithFactorsRef.current[hourSerieIndex].hourSerieType) {
        case HourSeriesType.HeatingDemand:
          heatingSumSerieRef.current = calcSeriesSumSerie(
            hourSeriesWithFactorsRef.current,
            HourSeriesType.HeatingDemand,
          )
          break
        case HourSeriesType.CoolingDemand:
          coolingSumSerieRef.current = calcSeriesSumSerie(
            hourSeriesWithFactorsRef.current,
            HourSeriesType.CoolingDemand,
          )
          break
        case HourSeriesType.ElectricityDemand:
          electricitySumSerieRef.current = calcSeriesSumSerie(
            hourSeriesWithFactorsRef.current.filter(
              (s) =>
                s.consumptionEnergyType !==
                  ConsumptionEnergyType.OccupantsElectricity &&
                s.consumptionEnergyType !==
                  ConsumptionEnergyType.PropertyElectricity,
            ),
            HourSeriesType.ElectricityDemand,
          )
          break

        default:
          break
      }

      updatedHourSeriesTypesRef.current = [
        hourSeries[hourSerieIndex].hourSerieType,
      ]

      dbUpdateNeededRef.current = true
    }

    correctionFactorsRef.current = updatedFactors

    setCorrectionFactors(updatedFactors)

    return true
  }

  const isSaveEnabled = () => dbUpdateNeededRef.current

  return (
    <Box marginTop={theme.spacing(1)}>
      {projectCalendarYear === null && (
        <Container maxWidth={false}>
          <Chip
            className={classes.warningContainer}
            label={t('buildingEnergyModels.summary.noShowMessage')}
            icon={<AlertCircle className={classes.chipIcon} />}
          />
        </Container>
      )}
      {projectCalendarYear !== null && (
        <>
          <Box>
            <Box
              mb={2}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h5">
                {t('buildingEnergyModels.summary.energyModelCharts.title')}
              </Typography>

              <Button
                variant="contained"
                color="primary"
                startIcon={<Save />}
                onClick={saveBem}
                disabled={!isSaveEnabled()}
              >
                {t('common.saveChanges')}
              </Button>
            </Box>

            <BemSummaryCharts
              model={buildingEnergyModel}
              year={projectCalendarYear}
              hourSeries={hourSeriesWithFactorsRef.current}
              updatedHourSeries={updatedHourSeriesTypesRef.current}
            />
          </Box>

          <Box mt={4}>
            <Box mb={2}>
              <Typography variant="h5">
                {t('buildingEnergyModels.summary.heating.title')}
              </Typography>
            </Box>
            {hourSeries.length > 0 ? (
              <BemSummaryTable
                model={buildingEnergyModel}
                hourSeries={hourSeriesWithFactorsRef.current.filter(
                  (s) => s.hourSerieType === HourSeriesType.HeatingDemand,
                )}
                sumSerie={heatingSumSerieRef.current}
                correctionFactors={getSeriesFactorData(
                  hourSeries,
                  correctionFactors,
                  HourSeriesType.HeatingDemand,
                )}
                originalHourSeriesSums={hourSeriesSumsWithoutFactors.current}
                onUpdateFactorData={updateFactorData}
              />
            ) : (
              <EmptyTableContainer
                text={t('buildingEnergyModels.summary.noTableData')}
              />
            )}
          </Box>

          <Box mt={6}>
            <Box mb={2}>
              <Typography variant="h5">
                {t('buildingEnergyModels.summary.cooling.title')}
              </Typography>
            </Box>
            {hourSeries.length > 0 ? (
              <BemSummaryTable
                model={buildingEnergyModel}
                hourSeries={hourSeriesWithFactorsRef.current.filter(
                  (s) => s.hourSerieType === HourSeriesType.CoolingDemand,
                )}
                sumSerie={coolingSumSerieRef.current}
                correctionFactors={getSeriesFactorData(
                  hourSeries,
                  correctionFactors,
                  HourSeriesType.CoolingDemand,
                )}
                originalHourSeriesSums={hourSeriesSumsWithoutFactors.current}
                onUpdateFactorData={updateFactorData}
              />
            ) : (
              <EmptyTableContainer
                text={t('buildingEnergyModels.summary.noTableData')}
              />
            )}
          </Box>

          <Box mt={6}>
            <Box mb={2}>
              <Typography variant="h5">
                {t('buildingEnergyModels.summary.electricity.title')}
              </Typography>
            </Box>
            {hourSeries.length > 0 ? (
              <BemSummaryElectricityTable
                model={buildingEnergyModel}
                hourSeries={hourSeriesWithFactorsRef.current.filter(
                  (s) => s.hourSerieType === HourSeriesType.ElectricityDemand,
                )}
                sumSerie={electricitySumSerieRef.current}
                correctionFactors={getSeriesFactorData(
                  hourSeries,
                  correctionFactors,
                  HourSeriesType.ElectricityDemand,
                )}
                originalHourSeriesSums={hourSeriesSumsWithoutFactors.current}
                onUpdateFactorData={updateFactorData}
              />
            ) : (
              <EmptyTableContainer
                text={t('buildingEnergyModels.summary.noTableData')}
              />
            )}
          </Box>

          <LoadingDialog isLoading={isUpdateLoading} />
        </>
      )}
    </Box>
  )
}
